import { findLast, isEmpty, isNil } from 'ramda';

import { PayPeriodType } from 'src/enums/PayPeriod';
import { TimeSheetState } from 'src/enums/TimeSheet';
import EngagementTimeSheet from 'src/types/resources/EngagementTimeSheet';
import TalentEngagementTimeSheetDates from 'src/types/resources/TalentEngagementTimeSheetDates';
import {
  NestedTimeSheet,
  TalentTimeSheetDates,
  TimeSheetDates,
  TimeSheetWithAdjustment,
} from 'src/types/resources/TimeSheet';
import { TimeTrackingFilters } from 'src/types/resources/TimeTracking';
import { PayPeriod } from 'src/types/resources/PayPeriod';
import { User } from 'src/types/resources/User';
import UsersPresenter from 'src/presenters/UsersPresenter';
import { eachDayOfInterval, formatTime, getDateByYear, isWithinInterval, parseISO } from 'src/utils/date';
import { isDateInPayPeriod } from 'src/utils/payPeriods';
import {
  addDaysToDate,
  formatPayPeriodClosesAt,
  getPayPeriodEndsAt,
  getPayPeriodStartsAt,
  getPayPeriodToday,
} from 'src/utils/payPeriodTime';

export const ORGANIZATION_GRACE_PERIOD = 7;

type DatePeriod = {
  start: Date;
  end: Date;
};

const getGracePeriodLastDay = (graceDayCount: number, currentPeriod: PayPeriod): string =>
  addDaysToDate(currentPeriod.startDate, graceDayCount - 1);

const isGracePayPeriod = (
  graceDayCount: number,
  currentPeriod: PayPeriod,
  weeklyPayPeriodsStartDate: string,
  now: Date,
): boolean => {
  if (currentPeriod.startDate > weeklyPayPeriodsStartDate) return false;

  const today = getPayPeriodToday(weeklyPayPeriodsStartDate, now);

  return currentPeriod.startDate <= today && today <= getGracePeriodLastDay(graceDayCount, currentPeriod);
};

const isOrganizationGracePayPeriod = (
  currentPeriod: PayPeriod,
  weeklyPayPeriodsStartDate: string,
  now: Date,
): boolean => isGracePayPeriod(ORGANIZATION_GRACE_PERIOD, currentPeriod, weeklyPayPeriodsStartDate, now);

export const isUserGracePeriod = (
  currentUser: User,
  currentPeriod: PayPeriod,
  weeklyPayPeriodsStartDate: string,
  now: Date = new Date(),
): boolean => {
  let isNowGracePayPeriod = false;

  if (UsersPresenter.isOrganizationUser(currentUser)) {
    isNowGracePayPeriod = isOrganizationGracePayPeriod(currentPeriod, weeklyPayPeriodsStartDate, now);
  }
  return isNowGracePayPeriod;
};

export const getEngagementTimeSheetsPeriod = (
  engagementTimeSheet: EngagementTimeSheet,
  payPeriod: DatePeriod,
): { startDate: Date; endDate: Date } => {
  const {
    startDate: engagementStartDate,
    endDate: engagementEndDate,
    terminatedByManagerAt: engagementTerminatedDate,
  } = engagementTimeSheet;
  const { start: payPeriodStartDate, end: payPeriodEndDate } = payPeriod;
  const finalEngagementEndDate = isNil(engagementTerminatedDate) ? engagementEndDate : engagementTerminatedDate;

  const engagementTimeSheetPeriodStartDate = isWithinInterval(parseISO(engagementStartDate), {
    start: payPeriodStartDate,
    end: payPeriodEndDate,
  })
    ? parseISO(engagementStartDate)
    : payPeriodStartDate;

  const engagementTimeSheetPeriodEndDate = isWithinInterval(parseISO(finalEngagementEndDate), {
    start: payPeriodStartDate,
    end: payPeriodEndDate,
  })
    ? parseISO(finalEngagementEndDate)
    : payPeriodEndDate;

  return {
    startDate: engagementTimeSheetPeriodStartDate,
    endDate: engagementTimeSheetPeriodEndDate,
  };
};

export const createEmptyTimeSheetEntry = (timekeepingDate: string, index: number): TimeSheetWithAdjustment => {
  return {
    id: 1000 + index,
    state: TimeSheetState.open,
    timekeepingDate,
    hours: '0.00',
    talentComment: null,
    clientComment: null,
    cost: null,
    costTalentFeeIncluded: null,
    enteredAt: null,
    reviewedAt: null,
    reviewedBy: null,
    purchaseOrder: null,
    children: null,
    isAdjustive: false,
    isOrganizationAdjustment: false,
  };
};

export const createEmptyEngagementTimeSheetDates = (date: Date, isEditablePayPeriod: boolean): TalentTimeSheetDates => {
  const timekeepingDate = getDateByYear(date.toISOString());

  return {
    id: date.getTime(),
    timekeepingDate,
    enteredHours: '0.00',
    approvedHours: '0.00',
    timesheets: isEditablePayPeriod ? [createEmptyTimeSheetEntry(timekeepingDate, 0)] : [],
  };
};

export const fillEngagementTimeSheetDates = (
  engagementTimeSheets: TalentEngagementTimeSheetDates[],
  payPeriod: DatePeriod,
  isEditablePayPeriod: boolean,
): TalentEngagementTimeSheetDates[] =>
  engagementTimeSheets.map(engagementTimeSheet => {
    // prettier-ignore
    const engagementTimeSheetDateMap = engagementTimeSheet.timesheetDates.reduce(
      (acc, timesheetDate) => {
        const date = parseISO(timesheetDate.timekeepingDate).getTime();
        return { ...acc, [date]: timesheetDate };
      },
      {} as Record<number, TalentTimeSheetDates>,
    );

    const engagementTimeSheetsPeriod = getEngagementTimeSheetsPeriod(engagementTimeSheet, payPeriod);

    const initialPayPeriodDates = eachDayOfInterval({
      start: engagementTimeSheetsPeriod.startDate,
      end: engagementTimeSheetsPeriod.endDate,
    });

    const resources = initialPayPeriodDates.map(date => {
      const currentTime = date.getTime();
      const foundTimeSheetDates = engagementTimeSheetDateMap[currentTime];
      const preparedFoundTimeSheetDates = { id: currentTime, ...foundTimeSheetDates };
      return isNil(foundTimeSheetDates)
        ? createEmptyEngagementTimeSheetDates(date, isEditablePayPeriod)
        : preparedFoundTimeSheetDates;
    });

    return { ...engagementTimeSheet, timesheetDates: resources };
  });

export const addIdToEngagementTimeSheetDates = (engagementTimeSheetDates: TimeSheetDates[]): TimeSheetDates[] => {
  return engagementTimeSheetDates.map(engagementTimeSheetDate => {
    return {
      id: new Date(engagementTimeSheetDate.timekeepingDate).getTime(),
      ...engagementTimeSheetDate,
    };
  });
};

export const getNextEmptyHoursInputElementIndex = (
  hoursInputElements: HTMLInputElement[],
  currentActiveIndex: number,
): number => {
  let nextActiveEmptyHoursElementIndex: number | null = null;

  hoursInputElements.forEach((hoursInputElement, index) => {
    const isNextActiveIndex =
      index > currentActiveIndex && isEmpty(hoursInputElement.value) && isNil(nextActiveEmptyHoursElementIndex);
    if (!isNextActiveIndex) return;
    nextActiveEmptyHoursElementIndex = index;
  });

  return nextActiveEmptyHoursElementIndex ?? -1;
};

// Mirrors the bulk approve queryset: Q(is_adjustive=False) | Q(is_last_adjustive=True, numchild=0).
export const isBulkApprovable = (timeSheet: NestedTimeSheet | TimeSheetWithAdjustment): boolean => {
  const { isLastAdjustive, numchild } = timeSheet as TimeSheetWithAdjustment;
  return !timeSheet.isAdjustive || (Boolean(isLastAdjustive) && !numchild);
};

// The list keeps a whole chain when any node of it matches, so a row on screen need not match
// the active filter - but the bulk approve applies Status and PO to each row. Counting a row the
// server will filter out would promise work that never gets approved.
export const matchesRowFilters = (timeSheet: NestedTimeSheet, filters: TimeTrackingFilters): boolean => {
  const states = filters.state?.split(',').filter(Boolean) ?? [];
  if (states.length > 0 && !states.includes(timeSheet.state)) {
    return false;
  }

  const poIds = filters.poIds?.split(',').filter(Boolean) ?? [];
  if (poIds.length > 0) {
    const poId = timeSheet.purchaseOrder?.purchaseOrder?.id;
    return !isNil(poId) && poIds.includes(String(poId));
  }

  return true;
};

export const isBulkApprovableUnderFilters = (
  timeSheet: NestedTimeSheet | TimeSheetWithAdjustment,
  filters: TimeTrackingFilters,
): boolean => {
  return isBulkApprovable(timeSheet) && matchesRowFilters(timeSheet, filters);
};

export const getTimeSheetPendingHours = (timeSheets: NestedTimeSheet[]): string => {
  const totalPendingHours = timeSheets
    .filter(timeSheet => timeSheet.state === TimeSheetState.pending)
    .reduce((acc, item) => acc + Number(item.hours), 0);

  return formatTime(String(totalPendingHours), { padStartHours: 1 });
};

export const isOriginalTimeSheet = (timeSheet: NestedTimeSheet): boolean => {
  return !('costDelta' in timeSheet && 'hoursDelta' in timeSheet);
};

export const getPayPeriodType = (
  payPeriodType: PayPeriodType,
): { isCurrentType: boolean; isGraceType: boolean; isCloseType: boolean } => {
  const isCurrentType = payPeriodType === PayPeriodType.current;
  const isGraceType = payPeriodType === PayPeriodType.grace;
  const isCloseType = payPeriodType === PayPeriodType.close;

  return { isCurrentType, isGraceType, isCloseType };
};

const getPayPeriodCountdownTimerLastDay = (payPeriodType: PayPeriodType, currentPeriod: PayPeriod): string => {
  const { isGraceType } = getPayPeriodType(payPeriodType);

  if (isGraceType) {
    return getGracePeriodLastDay(ORGANIZATION_GRACE_PERIOD, currentPeriod);
  }

  return currentPeriod.endDate;
};

export type PayPeriodCountdown = {
  remainingTime: number;
  daysDuration: number;
  payPeriodType: PayPeriodType;
  endDate: string;
};

type PayPeriodCountdownParams = {
  now: Date;
  payPeriodDate: Date;
  currentPeriod: PayPeriod;
  previousPeriod: PayPeriod | undefined;
  isGracePeriod: boolean;
  weeklyPayPeriodsStartDate: string;
};

export const getPayPeriodCountdown = (params: PayPeriodCountdownParams): PayPeriodCountdown => {
  const { now, payPeriodDate, currentPeriod, previousPeriod, isGracePeriod, weeklyPayPeriodsStartDate } = params;

  const isNowCurrentPayPeriod = isDateInPayPeriod(payPeriodDate, currentPeriod);
  const isNowGracePayPeriod =
    isGracePeriod && Boolean(previousPeriod && isDateInPayPeriod(payPeriodDate, previousPeriod));

  let payPeriodType = PayPeriodType.close;
  if (isNowCurrentPayPeriod) payPeriodType = PayPeriodType.current;
  if (isNowGracePayPeriod) payPeriodType = PayPeriodType.grace;

  const lastDay = getPayPeriodCountdownTimerLastDay(payPeriodType, currentPeriod);
  const endDate = getPayPeriodEndsAt(lastDay, weeklyPayPeriodsStartDate);
  const endDateString = formatPayPeriodClosesAt(lastDay, weeklyPayPeriodsStartDate);

  const startTime = now.getTime() / 1000;
  const endTime = endDate.getTime() / 1000;
  const periodStartTime = getPayPeriodStartsAt(currentPeriod.startDate, weeklyPayPeriodsStartDate).getTime() / 1000;

  const remainingTime = endTime - startTime;
  const daysDuration = endTime - periodStartTime;

  return { remainingTime, daysDuration, payPeriodType, endDate: endDateString };
};

export const getOrgAdjustableTimeSheet = (chain: NestedTimeSheet[]): NestedTimeSheet | null => {
  if (chain.some(timeSheet => timeSheet.state === TimeSheetState.pending)) return null;
  return findLast(timeSheet => timeSheet.state === TimeSheetState.approved, chain) ?? null;
};

export const isWeeklyPayPeriodsStarted = (
  weeklyPayPeriodsStartDate: string | null | undefined,
  now: Date = new Date(),
): boolean =>
  Boolean(weeklyPayPeriodsStartDate && getPayPeriodToday(weeklyPayPeriodsStartDate, now) >= weeklyPayPeriodsStartDate);

// From the weekly cutover the PO only routes the cost: it keeps changing after the review, and once
// the week has closed on approved time. Before it, only submitted time in an editable period.
export const isPOChangeable = (
  timeSheet: Pick<NestedTimeSheet, 'state' | 'isAdjustive'>,
  isInEditablePeriod: boolean,
  isWeeklyStarted: boolean,
): boolean => {
  const isPending = timeSheet.state === TimeSheetState.pending;
  const isApproved = timeSheet.state === TimeSheetState.approved;
  if (!isWeeklyStarted) return isPending && (timeSheet.isAdjustive || isInEditablePeriod);
  if (timeSheet.isAdjustive) return isPending || isApproved;
  return isInEditablePeriod || isApproved;
};
